import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { billing } from "@/lib/api";
import { useBalance } from "@/lib/balance-context";
import PageHead from "@/components/PageHead";

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refresh: refreshBalance } = useBalance();
  const [state, setState] = useState({ loading: true, mode: null, paid: false, error: null });

  useEffect(() => {
    let alive = true;
    let attempts = 0;
    const maxAttempts = 8;

    async function poll() {
      if (!sessionId) {
        setState({ loading: false, mode: null, paid: false, error: "Missing session id" });
        return;
      }
      try {
        const s = await billing.checkoutStatus(sessionId);
        attempts += 1;
        const paid = s.payment_status === "paid" || s.status === "complete";
        if (paid || attempts >= maxAttempts) {
          if (!alive) return;
          setState({ loading: false, mode: s.mode, paid, error: paid ? null : "Payment still processing" });
          if (paid) refreshBalance();
        } else {
          setTimeout(poll, 1500);
        }
      } catch (e) {
        if (!alive) return;
        setState({ loading: false, mode: null, paid: false, error: e?.response?.data?.detail || "Could not confirm payment" });
      }
    }
    poll();
    return () => {
      alive = false;
    };
  }, [sessionId, refreshBalance]);

  return (
    <div className="max-w-xl mx-auto px-6 md:px-10 py-24 text-center" data-testid="billing-success-page">
      <PageHead title="Payment confirmation | ChronoVaults" noindex />
      {state.loading ? (
        <>
          <Loader2 className="w-8 h-8 text-terracotta mx-auto animate-spin" strokeWidth={1.5} />
          <h1 className="font-serif text-4xl text-bone mt-8 leading-none">Confirming your payment…</h1>
          <p className="text-white/50 mt-4 text-sm">This usually takes a couple of seconds.</p>
        </>
      ) : state.paid ? (
        <>
          <CheckCircle2 className="w-10 h-10 text-terracotta mx-auto" strokeWidth={1.5} />
          <h1 className="font-serif text-4xl text-bone mt-6 leading-none" data-testid="billing-success-heading">
            {state.mode === "subscription" ? "Welcome to ChronoVaults membership." : "Message credit added."}
          </h1>
          <p className="text-white/60 mt-4 text-sm">
            {state.mode === "subscription"
              ? "Your £1 per-message rate is now unlocked. Cancel anytime from the customer portal."
              : "One paygo credit is now on your account. It never expires."}
          </p>
          <div className="mt-10 flex gap-3 justify-center">
            <Link to="/capsules/new">
              <Button
                data-testid="billing-success-compose"
                className="h-11 px-6 bg-terracotta hover:bg-terracotta/90 text-white rounded-sm"
              >
                Seal a capsule
              </Button>
            </Link>
            <Link to="/dashboard">
              <Button
                data-testid="billing-success-dashboard"
                variant="outline"
                className="h-11 px-6 border-white/20 bg-transparent text-bone hover:bg-white/5 rounded-sm"
              >
                Back to dashboard
              </Button>
            </Link>
          </div>
        </>
      ) : (
        <>
          <h1 className="font-serif text-4xl text-bone leading-none">Still working on it.</h1>
          <p className="text-white/60 mt-4 text-sm">
            {state.error || "Your payment is being confirmed by Stripe. Refresh in a moment or check your dashboard."}
          </p>
          <Link to="/dashboard" className="mt-8 inline-block">
            <Button className="h-11 px-6 bg-terracotta hover:bg-terracotta/90 text-white rounded-sm">
              Back to dashboard
            </Button>
          </Link>
        </>
      )}
    </div>
  );
}
